import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import API from "@/lib/api";

const SYNC_INTERVAL = 60_000; // 1 minute
let _allConfigs = null;     // in-memory cache shared across hook instances
let _fetchedFromServer = false;
let _dirty = false;
let _syncTimer = null;

// Load all configs from localStorage
function loadAll() {
  try {
    const raw = localStorage.getItem("table_configs");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// Save all configs to localStorage
function saveAll(configs) {
  try { localStorage.setItem("table_configs", JSON.stringify(configs)); } catch {}
}

// Sync to backend (fire-and-forget)
function syncToBackend() {
  if (!_dirty) return;
  _dirty = false;
  const configs = _allConfigs || loadAll();
  API.put("/preferences", { configs }).catch(() => { _dirty = true; });
}

// Fetch from backend once on first load, merge with local
async function fetchFromBackend() {
  if (_fetchedFromServer) return;
  _fetchedFromServer = true;
  try {
    const res = await API.get("/preferences");
    const serverConfigs = res.data?.configs || {};
    const localConfigs = loadAll();
    // Local wins if it exists (user made changes offline), otherwise server fills in
    const merged = { ...serverConfigs, ...localConfigs };
    _allConfigs = merged;
    saveAll(merged);
  } catch {}
}

// Start background sync timer (singleton)
function ensureSyncTimer() {
  if (_syncTimer) return;
  _syncTimer = setInterval(syncToBackend, SYNC_INTERVAL);
  // Also sync on page unload
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", syncToBackend);
  }
}

/**
 * Hook for table column config. localStorage is primary, backend syncs async.
 */
export default function useTableConfig(tableId, columns) {
  // Initialize from localStorage
  const [config, setConfig] = useState(() => {
    if (!_allConfigs) _allConfigs = loadAll();
    return _allConfigs[tableId] || {};
  });

  // Fetch from server once
  const didFetch = useRef(false);
  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchFromBackend().then(() => {
      const all = _allConfigs || loadAll();
      if (all[tableId]) setConfig(all[tableId]);
    });
    ensureSyncTimer();
  }, [tableId]);

  // Write to localStorage + mark dirty on every change
  useEffect(() => {
    if (!_allConfigs) _allConfigs = loadAll();
    _allConfigs[tableId] = config;
    saveAll(_allConfigs);
    _dirty = true;
  }, [config, tableId]);

  const visible = useMemo(() => config.visible || {}, [config.visible]);
  const widths = useMemo(() => config.widths || {}, [config.widths]);
  const filters = useMemo(() => config.filters || {}, [config.filters]);

  const setVisible = useCallback((v) => setConfig(p => ({ ...p, visible: v })), []);
  const setWidth = useCallback((k, w) => setConfig(p => ({ ...p, widths: { ...(p.widths || {}), [k]: w } })), []);
  const show = useCallback((k) => visible[k] !== false, [visible]);
  const getWidth = useCallback((k) => {
    if (widths[k]) return widths[k];
    const col = columns.find(c => c.key === k);
    return col?.defaultWidth || undefined;
  }, [widths, columns]);
  const getFilter = useCallback((k) => filters[k] || [], [filters]);
  const setFilter = useCallback((k, v) => setConfig(p => ({ ...p, filters: { ...(p.filters || {}), [k]: v } })), []);

  return { visible, setVisible, widths, setWidth, show, getWidth, filters, getFilter, setFilter };
}
